// THE ONE ADAPTER. The Automated Trading surface has two possible sources — the live research
// feed (the v2 `trading` block) and the illustrative book in tradingData.js — and this file is
// the single place their two shapes are reconciled into the LIST of account view-models the
// components read. Because the reconciliation lives here, no child component branches on where
// the numbers came from: AutomatedTrading, AccountDetail, AllocationBlock and MiniTwrChart all
// consume the same bundle either way.
//
// SINGLE-OR-ARRAY, ONE PATH (the future-proofing). The feed's `trading` block is a SINGLE
// account object today and becomes MULTIPLE accounts later, and we deliberately do NOT hardcode
// which container shape the source will settle on. `normalizeAccounts` accepts every plausible
// shape and always yields a flat list of raw account objects, so the day the source switches to
// an array — or wraps them under `accounts` — nothing downstream changes and there is no flag
// day. This is the single seam where that decision is absorbed; keep it here and nowhere else.
//
// THE DISPLAY NAME COMES FROM THE SOURCE, with a local override as an escape hatch. The push
// carries a per-account `display_name` (the shareholder-facing name, e.g. "Genesis", "Nautilus"),
// and we render that. Naming is a presentation choice set once at the source, and we own both
// portals, so the source is the single place it lives — no per-account edit-and-deploy here.
// `ACCOUNT_NAME_MAP` below is an OPTIONAL local override (empty by default) that WINS over the
// source, so the portal can correct or neutralise a name without a source round-trip.
// Precedence: local override → source display_name → a safe placeholder.
//
// SCRUB: the raw wire `account_label` is NEVER rendered — not even as a fallback. It survives ONLY
// as the join key (and as opaque data on the VM); if neither an override nor a source display_name
// is present, a neutral placeholder is shown, never the internal label. displayName is the single
// string every surface shows.
//
// TWO WIRE SHAPES, ONE VM (no flag day). The `trading` account object is migrating and BOTH
// shapes must render:
//   - OLD (live today): { aum (= the deployed book), cash (POSITIVE, uninvested), total_pnl,
//     sharpe, volatility, curve[], holdings[], buckets[] }. Here `aum` MEANS the deployed book
//     and `cash` is spare cash, so invested = aum − cash and the KPI tiles read AUM / Cash.
//   - NEW (coming): detected by the presence of `gross_positions` OR `infused`. Here the SAME
//     two keys change MEANING: `aum` becomes NAV/equity (infused + P&L, what the book is worth)
//     and `cash` becomes NET FINANCING (infused − deployed, NEGATIVE under leverage). The book
//     also gains `gross_positions` (the deployed book, Σ strategy equity — exceeds NAV when
//     levered), `infused` (external capital in) and `basket_performance[]` (the expandable table).
// The adapter absorbs the difference: `isNewShape` is set when either new key is present, and the
// VM exposes NEUTRAL names — `nav`, `infused`, `grossPositions`, `netFinancing`, `levered` — so a
// component branches on `isNewShape`, never on which key carried what. OLD-shape figures (aum,
// cash, cashPct, invested, buckets) are left byte-identical so today's live payload is unchanged.
//
// WHAT THE WIRE SENDS vs WHAT IS COMPUTED HERE. The v2 block sends `sharpe`/`volatility`
// directly (so a real block uses those); the illustrative book computes them from its own
// weekly-return curve (so ILLUSTRATIVE keeps them). Everything else the contract deliberately
// omits — cash-%, per-holding allocation-%, bucket allocation-%, per-holding total P&L
// (realised + unrealised), and the since-inception %returns the list shows — is derived
// CLIENT-SIDE here, from either source, so a changed leaf can never leave a stale percentage
// beside it. Derivation is NOT moved onto the wire.
//
// The `buckets` array drives the Bucket view and is taken as sent — it is NEVER re-derived from
// holdings.strategy_class (a holding's opening class is not the same partition as the book's
// strategy buckets). The per-symbol `holdings` drive the Security and Sector views. `curve[]`
// arrives as un-rebased cumulative TWR decimals and is rebased to the window start here, which
// is the shape MiniTwrChart expects.

import {
  AUM, CASH, CASH_PCT, TOTAL_PNL, SHARPE, ANN_VOL, AS_AT,
  EQUITY_CURVE, BENCHMARK_CURVE, HOLDINGS, BY_SECTOR, BY_BUCKET, BOOK_PNL,
} from './tradingData';

const num = (v) => (typeof v === 'number' && isFinite(v) && typeof v !== 'boolean' ? v : null);

// Normalize a wire `profit_by_period` object into the four period keys the pill toggles, each a
// signed USD number or null (never fabricated). Absent object → null, so a consumer can fall back
// to since-inception profit. Boundaries are the SOURCE's; ours match them (Monday-start ISO week,
// calendar YTD/QTD/MTD, all UTC) so the period Profit lines up with the Return/SPX/Alpha toggle.
const profitByPeriod = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  return { ytd: num(obj.ytd), qtd: num(obj.qtd), mtd: num(obj.mtd), wtd: num(obj.wtd) };
};

// Local display-name OVERRIDES — win over the source's per-account `display_name`. Empty by
// default: the source sends the name (e.g. Live_Mahroof_1 → "Genesis"), and this exists only to
// correct one from the portal side without a source change. Exact-match on account_label,
// deterministic (no fuzzy matching — house rule).
export const ACCOUNT_NAME_MAP = {};

// The shareholder-facing name: local override → the source's display_name → a safe placeholder.
// The raw account_label is NEVER used as a fallback (house rule); it is only the lookup key.
const displayNameFor = (wire) => {
  const label = wire && wire.account_label;
  const src = wire && typeof wire.display_name === 'string' ? wire.display_name.trim() : '';
  return ACCOUNT_NAME_MAP[label] || src || 'Portfolio';
};

// Last defined value of a field across a curve, scanning from the end — the since-inception
// return for the list. The benchmark can end null (a gap at the window edge), so the last
// DEFINED point is taken, not blindly the last element.
const lastDefined = (curve, field) => {
  const rows = Array.isArray(curve) ? curve : [];
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const v = num(rows[i] && rows[i][field]);
    if (v != null) return v;
  }
  return null;
};

// The illustrative account: the existing derived exports, re-shaped to the view-model keys the
// components read. Built from tradingData's own leaves, so it stays byte-identical to the page
// as it renders today — the fallback must not change a single figure. `invested` = AUM − cash
// (= the deployed book here, since illustrative cash is positive), which is what the list's
// per-bucket allocation-% divides by.
export const ILLUSTRATIVE = {
  isReal: false,
  accountLabel: null,
  displayName: 'Illustrative Portfolio',
  description: null,   // no source description / inception for the illustrative book
  inceptionDate: null,
  asAt: AS_AT,
  dataAsOf: AS_AT,     // illustrative send-time and data-date are the same fixed instant
  benchmarkLabel: 'SPX',
  aum: AUM,
  cash: CASH,
  cashPct: CASH_PCT,
  invested: AUM - CASH,
  // NEW-shape fields — the illustrative book is the OLD shape, so these are absent/false and the
  // detail renders the AUM/Cash tiles and the buckets children, exactly as before.
  isNewShape: false,
  isLevered: false,
  nav: AUM,             // NAV == aum in both shapes (here aum IS the illustrative book's worth)
  infused: null,
  grossPositions: null,
  netFinancing: null,
  levered: null,
  basketPerformance: null,
  totalPnl: TOTAL_PNL,
  profitByPeriod: null,  // illustrative book has no per-period profit → Profit stays since-inception
  sharpe: SHARPE,
  annVol: ANN_VOL,
  pctReturn: lastDefined(EQUITY_CURVE, 'value'),      // since inception, fraction
  benchReturn: lastDefined(BENCHMARK_CURVE, 'value'), // benchmark since inception, fraction
  equityCurve: EQUITY_CURVE,
  benchmarkCurve: BENCHMARK_CURVE,
  holdings: HOLDINGS,
  bySector: BY_SECTOR,
  byBucket: BY_BUCKET,
  bookPnl: BOOK_PNL,
};

// The fallback is a ONE-account illustrative list, so the list view has the same shape whether
// the data is real or illustrative — the caller never special-cases "no real data" beyond
// choosing this list and showing the banner.
export const ILLUSTRATIVE_LIST = [ILLUSTRATIVE];

// Group holdings' market value by an arbitrary key (used for Sector), share of the deployed
// book, DESC by value — the same derivation tradingData does, applied to wire holdings.
//
// An empty key is NOT dropped when a `fallbackLabel` is given: it is folded into that residual
// bucket so the grouping RECONCILES to the whole book. Previously empties were discarded, which
// on the live book silently dropped the ETF sleeve (SPY + the SPDR sector ETFs, ~74% of the
// deployed book — the source sends ETFs with no GICS sector) — so the Sector text summed to ~26%
// while the donut normalised its slices to fill the circle and looked like 100%. That divergence
// is what a reader saw. The correct sector tags are the SOURCE's to send (an ETF→sector attribution
// is theirs to own, and SPY is genuinely multi-sector); until then we show the untagged remainder
// honestly as one "Unclassified" slice so text and donut agree and nothing is hidden.
function groupBy(holdings, key, deployed, fallbackLabel = null) {
  const m = new Map();
  holdings.forEach((h) => {
    const raw = h[key];
    const label = (raw != null && String(raw).trim()) || fallbackLabel;
    if (!label) return;  // no fallback → empties are dropped (prior behaviour, for callers that want it)
    m.set(label, (m.get(label) || 0) + h.marketValue);
  });
  return [...m.entries()]
    .map(([label, marketValue]) => ({ label, marketValue, pct: deployed ? marketValue / deployed : 0 }))
    .sort((a, b) => b.marketValue - a.marketValue);
}

// Rebase one cumulative-return series in the wire curve to the window start (value 0 = first
// point that carries the series), which is what MiniTwrChart plots. The portfolio series is
// present throughout; the benchmark may begin null, so the base is the first non-null point and
// a null stays null (MiniTwrChart skips null benchmark points by date).
function rebase(curve, field) {
  const rows = Array.isArray(curve) ? curve : [];
  let base = null;
  for (const p of rows) { const v = num(p && p[field]); if (v != null) { base = v; break; } }
  return rows.map((p) => {
    const v = num(p && p[field]);
    return { date: p && p.date, value: v == null || base == null ? null : (1 + v) / (1 + base) - 1 };
  });
}

// Does this object look like one account? Used both to decide whether a bare object IS a single
// account and to filter junk out of an accounts array. An account carries at least one of the
// fields the detail reads; an empty object or a container wrapper does not.
function looksLikeAccount(o) {
  return !!o && typeof o === 'object' && !Array.isArray(o)
    && ('account_label' in o || 'holdings' in o || 'curve' in o || 'buckets' in o || 'aum' in o);
}

// Normalize the `trading` block into a flat list of RAW account objects, whatever container the
// feed uses now or later. ONE path handles every shape so there is never a flag day:
//   - an array                       → those are the accounts (the eventual multi-account shape)
//   - an object with an `accounts` [] → use that array (a possible future container)
//   - a bare account object          → wrap as a one-item list (TODAY: single `account_label`)
//   - null / empty / anything else   → [] (no real data → caller uses the illustrative list)
// We refuse to assume which of the first three the source will settle on, so the switch costs
// nothing downstream.
export function normalizeAccounts(trading) {
  if (Array.isArray(trading)) return trading.filter(looksLikeAccount);
  if (trading && typeof trading === 'object') {
    if (Array.isArray(trading.accounts)) return trading.accounts.filter(looksLikeAccount);
    if (looksLikeAccount(trading)) return [trading];
  }
  return [];
}

// Reconcile ONE raw account object into the view-model the components read (the same shape as
// ILLUSTRATIVE). Returns null for an unusable object, so a bad row is dropped rather than
// rendered blank. `asAtFromFeed` is the read endpoint's `as_at` (the snapshot's generated_at) —
// preferred over the block's own `as_at` so the dateline matches the rest of the feed surface.
export function adaptAccount(wire, asAtFromFeed) {
  if (!looksLikeAccount(wire)) return null;

  const rawHoldings = Array.isArray(wire.holdings) ? wire.holdings : [];
  const base = rawHoldings.map((h) => {
    const realized = num(h.realized_pnl) || 0;
    const unrealized = num(h.unrealized_pnl) || 0;
    return {
      symbol: (h.symbol || '').toString(),
      name: (h.name || '').toString(),
      sector: (h.sector || '').toString(),
      bucket: (h.strategy_class || '').toString(),  // label only; NOT used to build byBucket
      marketValue: num(h.market_value) || 0,
      cost: num(h.cost_basis),
      realized,
      unrealized,
    };
  });

  const deployed = base.reduce((t, h) => t + h.marketValue, 0);

  // Per-holding total P&L and allocation-% are computed here (the contract omits them).
  const holdings = base
    .map((h) => ({ ...h, pnl: h.realized + h.unrealized, pct: deployed ? h.marketValue / deployed : 0 }))
    .sort((a, b) => b.marketValue - a.marketValue);

  // 'Unclassified' residual so the sector view reconciles to the whole book (see groupBy) — the
  // source-untagged ETF sleeve lands here until the source sends sector tags.
  const bySector = groupBy(holdings, 'sector', deployed, 'Unclassified');

  // Bucket view comes from the wire `buckets` array as sent; only the allocation-% is derived.
  // A "Reserve"/"Unallocated" label is just another {label, market_value} row and renders fine.
  const rawBuckets = Array.isArray(wire.buckets) ? wire.buckets : [];
  const byBucket = rawBuckets
    .map((b) => ({
      label: (b.label || '').toString(),
      marketValue: num(b.market_value) || 0,
    }))
    .map((b) => ({ ...b, pct: deployed ? b.marketValue / deployed : 0 }))
    .sort((a, b) => b.marketValue - a.marketValue);

  const bookPnl = {
    realized: holdings.reduce((t, h) => t + h.realized, 0),
    unrealized: holdings.reduce((t, h) => t + h.unrealized, 0),
    total: holdings.reduce((t, h) => t + h.pnl, 0),
  };

  const aum = num(wire.aum);
  const cash = num(wire.cash);
  const cashPct = aum != null && aum !== 0 && cash != null ? (cash / aum) * 100 : null;
  // Invested capital = AUM − cash. NEGATIVE cash (margin/leverage, as the live book carries)
  // makes invested EXCEED AUM, which is correct: market exposure can be bigger than equity. The
  // list divides bucket market values by this to show each bucket's share of what is invested.
  // OLD shape only — in the NEW shape the buckets children are replaced by basket_performance.
  const invested = aum != null && cash != null ? aum - cash : null;

  // NEW-shape detection: the revised feed carries `gross_positions` and/or `infused`. When
  // present, `aum` MEANS NAV and `cash` MEANS net financing (see the header note) — so we expose
  // neutral names the detail branches on, never re-interpreting aum/cash in a component.
  const isNewShape = ('gross_positions' in wire) || ('infused' in wire);
  const grossPositions = num(wire.gross_positions);   // the deployed book (Σ strategy equity)
  const infused = num(wire.infused);                  // external capital actually put in
  const nav = aum;                                    // == aum in both shapes; MEANS NAV when new
  const netFinancing = isNewShape ? cash : null;      // cash in the new shape; negative = margin
  // Leverage = gross positions / NAV (e.g. 1.59×). Only meaningful in the new shape, and only
  // when both legs are known and NAV is non-zero — otherwise null (never a divide-by-zero or a
  // fabricated ratio). isLevered is the honest "gross exceeds NAV" flag the detail can read.
  const levered = isNewShape && grossPositions != null && nav != null && nav !== 0
    ? grossPositions / nav : null;
  const isLevered = levered != null && levered > 1;

  // basket_performance[] — the NEW array the source calls "render as an expandable table under
  // the account". Mapped to neutral VM keys; SOURCE ORDER preserved (pre-sorted by
  // aum_pct_of_gross desc — we do NOT re-sort). null when absent → the tree keeps buckets[].
  const rawBaskets = Array.isArray(wire.basket_performance) ? wire.basket_performance : null;
  const basketPerformance = rawBaskets
    ? rawBaskets.map((b) => ({
      label: (b.label || '').toString(),
      strategyClass: (b.strategy_class || '').toString(),
      nStrategies: num(b.n_strategies),
      pctOfGross: num(b.aum_pct_of_gross),    // fraction; ×100 for display, sums to ~100%
      profit: num(b.profit),                  // $ P&L for the basket, signed (since inception)
      profitByPeriod: profitByPeriod(b.profit_by_period), // signed $ per period; null → use `profit`
      pnlPctOfNav: num(b.pnl_pct_of_nav),     // fraction; ×100 for display
      startDate: b.start_date || null,
    }))
      // Drop a basket only when it is BOTH inert (holds ~0% of gross now) AND has made ~nothing
      // (profit ≈ 0). A closed basket that still carries realised P&L is kept — its contribution
      // is real history. This removes a fully-wound-down, never-earned basket (e.g. Nautilus'
      // duplicate Trend Dip Buyer at 0% weight / $0) that would otherwise show as a dead row.
      // Removing a 0%-weight, $0 basket cannot disturb the weight (still sums to ~100%) or any P&L.
      .filter((b) => {
        const inert = b.pctOfGross == null || Math.abs(b.pctOfGross) < 1e-9;
        const noProfit = b.profit == null || Math.abs(b.profit) < 0.005;
        return !(inert && noProfit);
      })
    : null;

  const equityCurve = rebase(wire.curve, 'portfolio_cum_return')
    .filter((p) => p.date && p.value != null);      // the chart needs numeric portfolio points
  const benchmarkCurve = rebase(wire.curve, 'benchmark_cum_return');

  const label = wire.account_label || null;
  return {
    isReal: true,
    accountLabel: label,
    displayName: displayNameFor(wire),
    // Source-sent, per account: what the account IS (a sentence shown as a footnote) and when it
    // started. Both optional — description is null until the source ships it (then it renders in
    // place of the generic basket note); inceptionDate drives the "Since …" line.
    description: (typeof wire.description === 'string' && wire.description.trim()) || null,
    inceptionDate: wire.inception_date || null,
    asAt: asAtFromFeed || wire.as_at
      || (equityCurve.length ? equityCurve[equityCurve.length - 1].date : null),
    // The DATA's as-of date — distinct from asAt above. asAt is the push's send time
    // (the snapshot generated_at, preferred for a dateline consistent with the other feed
    // surfaces); dataAsOf is the date the FIGURES actually reflect (the trading block's own
    // `as_at` — the close the curve and profit_by_period are computed against). The two differ
    // whenever a push is generated after the data's close: a Monday-morning push carries
    // Friday's book. Period windows MUST anchor to dataAsOf, not asAt — anchoring WTD to the
    // Monday send time points at a not-yet-started week (empty return) while profit_by_period,
    // computed as-of Friday, still reports the prior week. Falls back to the send time, then the
    // last curve date, so it is never null when any date is known.
    dataAsOf: wire.as_at || asAtFromFeed
      || (equityCurve.length ? equityCurve[equityCurve.length - 1].date : null),
    benchmarkLabel: wire.benchmark_label || 'SPX',
    aum,
    cash,
    cashPct,
    invested,
    // NEW-shape neutral fields (null/false on the OLD shape, so the OLD path is unchanged).
    isNewShape,
    isLevered,
    nav,
    infused,
    grossPositions,
    netFinancing,
    levered,
    basketPerformance,
    totalPnl: num(wire.total_pnl),                  // sent by the wire; not re-summed from holdings
    profitByPeriod: profitByPeriod(wire.profit_by_period), // signed $ per period; null → use totalPnl
    sharpe: num(wire.sharpe),                       // wire sends it directly
    annVol: num(wire.volatility),                   // annualised decimal, sent directly
    // Since-inception %returns for the list — the last DEFINED cumulative return on the wire
    // curve (un-rebased decimals), not the rebased chart series, per the contract.
    pctReturn: lastDefined(wire.curve, 'portfolio_cum_return'),
    benchReturn: lastDefined(wire.curve, 'benchmark_cum_return'),
    equityCurve,
    benchmarkCurve,
    holdings,
    bySector,
    byBucket,
    bookPnl,
  };
}

// Reconcile a `trading` block (any container shape) into a LIST of account view-models. Returns
// null when no usable real account is present, so the caller falls back to ILLUSTRATIVE_LIST and
// shows the banner — the single decision "is any of this real?" made in one place.
export function adaptTradingList(trading, asAtFromFeed) {
  const raw = normalizeAccounts(trading);
  const vms = raw.map((a) => adaptAccount(a, asAtFromFeed)).filter(Boolean);
  return vms.length ? vms : null;
}
